/* eslint-disable react/prop-types */
import { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

import api from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";
import { fadeInUp, fadeIn, stagger } from "../../utils/animations";

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const { user, loading: authLoading } = useContext(AuthContext);

  const [data, setData] = useState({
    institutions: 0,
    textes: 0,
    notifications: 0,
    utilisateurs: 0,
    textes_analyses: 0,
    nouveaux_textes: 0,
    total_exigences: 0,
    exigences_par_domaine: [],
    procedures_actives: 0,
    taux_couverture: 0,
    exigences_total: 0,
    exigences_couvertes: 0,
    ecarts_signales: 0,
    risques_par_niveau: {},
    plans_en_retard: 0,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const { data: d } = await api.get("/api/dashboard");
        setData((prev) => ({ ...prev, ...d }));
      } catch {
        setFetchError("Impossible de charger les données du tableau de bord.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} sx={{ animation: `${fadeIn} 0.4s ease-out` }}>
        {fetchError && (
          <MDBox mb={3}>
            <Alert severity="error">{fetchError}</Alert>
          </MDBox>
        )}

        {/* ── Cartes générales ── */}
        <Grid container spacing={3}>
          {[
            {
              icon: "account_balance",
              title: "Institutions",
              count: data.institutions,
              label: "Partenaires enregistrés",
            },
            {
              icon: "gavel",
              title: "Textes Réglementaires",
              count: data.textes,
              label: "Documents disponibles",
            },
            {
              icon: "notifications",
              title: "Notifications",
              count: data.notifications,
              label: "Alertes actives",
            },
            {
              icon: "person_add",
              title: "Utilisateurs",
              count: data.utilisateurs,
              label: "Comptes actifs",
            },
            {
              icon: "analytics",
              title: "Textes analysés",
              count: data.textes_analyses,
              label: "Textes avec statut Analysé",
            },
            {
              icon: "fiber_new",
              title: "Nouveaux textes",
              count: data.nouveaux_textes,
              label: "Ajoutés ces 3 derniers mois",
            },
            {
              icon: "rule",
              title: "Total Exigences",
              count: data.total_exigences,
              label: "Sur textes actifs uniquement",
            },
          ].map((card, i) => (
            <Grid item xs={12} md={6} lg={3} key={card.title}>
              <MDBox
                mb={1.5}
                sx={{
                  animation: `${fadeInUp} 0.5s ease-out ${stagger(i, 0.08)} both`,
                  transition: "transform 0.3s, box-shadow 0.3s",
                  borderRadius: "12px",
                  borderLeft: "6px solid #D89A1F",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(216,154,31,0.12)",
                  },
                }}
              >
                <ComplexStatisticsCard
                  color="purple"
                  icon={card.icon}
                  iconColor="#FFD740"
                  title={card.title}
                  count={card.count}
                  percentage={{ color: "success", amount: "", label: card.label }}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        {/* ── Histogramme exigences par domaine ── */}
        {data.exigences_par_domaine.length > 0 && (
          <MDBox mt={6} sx={{ animation: `${fadeInUp} 0.5s ease-out 0.5s both` }}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2.5}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Icon sx={{ color: "#FFD740", fontSize: "22px !important" }}>bar_chart</Icon>
                <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                  Exigences par domaine
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", ml: 1 }}>
                  - textes actifs uniquement
                </MDTypography>
                <MDBox ml="auto">
                  <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                    Total : {data.total_exigences}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox pt={2.5} pb={2} px={3}>
                {[...data.exigences_par_domaine]
                  .sort((a, b) => b.total - a.total)
                  .map((row, idx, arr) => {
                    const pct =
                      data.total_exigences > 0 ? (row.total / data.total_exigences) * 100 : 0;
                    const isLast = idx === arr.length - 1;
                    return (
                      <MDBox
                        key={row.domaine}
                        sx={{
                          animation: `${fadeInUp} 0.35s ease-out ${stagger(idx, 0.07)} both`,
                          pb: isLast ? 0 : 1.8,
                          mb: isLast ? 0 : 1.8,
                          borderBottom: isLast ? "none" : "1px solid #f0f2f5",
                        }}
                      >
                        {/* Label row */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={0.8}
                        >
                          <MDBox display="flex" alignItems="center" gap={0.8}>
                            <Icon
                              sx={{ color: "#186A81", fontSize: "15px !important", flexShrink: 0 }}
                            >
                              domain
                            </Icon>
                            <MDTypography
                              variant="caption"
                              fontWeight="medium"
                              color="dark"
                              sx={{ fontSize: "0.78rem" }}
                            >
                              {row.domaine}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ color: "#0A3475", fontSize: "0.8rem" }}
                            >
                              {row.total}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{ color: "#9e9e9e", fontSize: "0.72rem" }}
                            >
                              ({pct.toFixed(0)}%)
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        {/* Bar track */}
                        <MDBox
                          sx={{
                            width: "100%",
                            height: 10,
                            borderRadius: "6px",
                            bgcolor: "#f0f2f5",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <MDBox
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: `${Math.max(pct, 1.5)}%`,
                              background: `linear-gradient(90deg, #0A3475 0%, #186A81 60%, #1a9bb5 100%)`,
                              borderRadius: "6px",
                              transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
                            }}
                          />
                        </MDBox>
                      </MDBox>
                    );
                  })}
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
